import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, App, Button, Form, Spin, Switch } from 'antd';

import EventFormElement from '../events/EventFormElement';
import RecordBadge from '../common/RecordBadge';
import ContextBar from '../common/ContextBar';
import { DataState, Skeleton, deriveDataState } from '../loading';
import FormActionsFooter from '../common/FormActionsFooter';
import { collectionCrumbs } from '../../utils/breadcrumbs';
import { PLUS, STACK, TRASH } from '../icons';
import { useDirtyForm } from '../../hooks/useDirtyForm';
import useCollectionAccess from '../../hooks/useCollectionAccess';
import useLinkedItems from '../../hooks/useLinkedItems';
import useUnitSystem from '../../hooks/useUnitSystem';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import { useCollectionContext } from '../../contexts/CollectionContext';
import { getMultiAddForm, seedMultiAddForm, submitMultiAddForm } from '../../services/multiAddForms';
import useMultiAddSignature, { itemsOnCards } from './useMultiAddSignature';
import { filledRowsOf, inputAsElement, stepLabelOf } from './multiAddFilledRows';
import { getUsableTags } from '../../services/tags';
import { applyPrivateTags } from '../../services/tagApplications';
import LaunchTagsRow from '../tags/LaunchTagsRow';
import useActiveTag from '../../hooks/useActiveTag';
import { launchTagsNote, privateTagRefusal } from '../../utils/itemTags';
import { fieldWidthClass, getElementInitialValue, getMissingRequiredElements, isElementValueEmpty } from '../../utils/elements';
import { collectionZone, collectionZoneKnown } from '../../utils/collectionTimezone';
import { mergedElementsForCard } from '../../utils/formulaTokens';
import { normalizeId } from '../../utils/ids';
import { createLookupResolver } from '../../utils/lookups';
import { stepCardsOf } from './MultiAddButtons';
import { useScreenShortcuts } from '../../contexts/ShortcutsContext';
import { escapeBelongsElsewhere } from '../../utils/escapeOwner';

/**
 * The multi add page: ONE form that adds several items of several cards.
 * Loads the form by id (a launch, a pasted link, a reload — all the same),
 * renders one panel per step with the step's ASKED elements (the mapped
 * ones are filled on the API from the inputs, from earlier steps, from
 * fixed values, or with the items earlier steps create), a leading panel
 * for the form's ad hoc inputs, and repeat steps as instances with "Add
 * another" / "Remove". The values live here only until submit: the API
 * builds and writes the items; nothing is drafted.
 *
 * Steps are REQUIRED or OPTIONAL (`step.required`). A required step is in
 * every submission; an optional one is included by its switch, which also
 * follows the typing — entering a value turns the step on, clearing every
 * value turns it off — so the common case needs no switch at all. An
 * excluded step is sent as `[]` (no item), its required elements are not
 * enforced, and anything a later step takes from it resolves to blank —
 * which the page refuses ahead of the API when the target is required.
 *
 * Submit is all-or-nothing on the API's side for anything a card refuses
 * (a 400 with per-field errors, nothing written). A write that fails after
 * earlier items landed comes back `partial`: the page shows what exists,
 * with links, and stops — resubmitting would add the first items twice.
 * A card asking for a signature is signed once per submit (useMultiAddSignature, KPR-1725).
 */

export { filledRowsOf, inputAsElement };

const isBlank = v => v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
const isDriven = element => !!element?.options?.drivenFrom;
// A "Me too" count is never asked on a form (KPR-390): a new item starts with
// nobody's confirmation, and the editor maps nothing to it.
const isCounted = element => element?.dataType === 'confirmations';
const labelOf = element => element?.label?.singular || element?.name || '';
const fieldKey = (step, index, name) => (step === null ? `input:${name}` : `${step}:${index}:${name}`);
const fieldDomId = (step, index, name) => `multi-add-field-${step === null ? 'input' : `${step}-${index}`}-${name}`;
// Deep-equal for stored values (plain JSON: strings, numbers, arrays of ids,
// { value, unit } measurements) — enough to tell "still the seeded default".
const sameValue = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

// The shared inputs derive their DOM ids from the element NAME, and this
// page renders the same name many times over (person on Blood pressure AND
// Heart rate, sku on every inventory line). Each rendered element gets a
// scoped name so labels and ids stay unique; the scope is stripped before
// the value is stored. (The side effect — a measurement's last-used unit is
// remembered per scope rather than per element — is harmless.)
const SCOPE_MARK = '__ma-';
export const scopedName = (name, step, index) => `${name}${SCOPE_MARK}${step === null ? 'input' : `${step}-${index}`}`;
export const unscopedName = name => String(name).split(SCOPE_MARK)[0];

// The ad hoc inputs are not a card, but the lookup pool resolves references
// per card; this pseudo card id stands in for "the inputs" there.
const INPUTS_CARD = '__multi-add-inputs__';

/** The elements a step ASKS for: merged card elements minus driven, minus counted, minus mapped. */
export function askedElementsOf(step, mergedElements) {
	const fields = step?.fields || {};
	return (mergedElements || []).filter(el => el?.name && !isDriven(el) && !isCounted(el) && !fields[el.name]);
}

/** The elements a step fills on its own (any mapping but omit), for the "filled for you" list. */
export function filledElementsOf(step, mergedElements) {
	const fields = step?.fields || {};
	return (mergedElements || []).filter(el => el?.name && fields[el.name] && fields[el.name].from !== 'omit');
}

// `zone` is the collection's (the owner's ruling B11): today, now and a
// relative date seed as the server would store them, never in the browser's
// zone.
const seedElements = (elements, zone) => {
	const out = {};
	elements.forEach(el => { out[el.name] = getElementInitialValue(el, { isNewRecord: true, zone }); });
	return out;
};

// An instance keeps the defaults it was seeded with beside its live values,
// so "still the seeded default" is a comparison against THAT seed — a
// date-time that defaults to now is a different instant every time it is
// recomputed, and would otherwise read as entered the moment anything else
// in the step changed.
const newInstance = (asked, zone) => {
	const elements = seedElements(asked, zone);
	return { elements, seeded: elements };
};

/** The starting values: card defaults for every asked element, `max(min, 1)` instances per repeat step. */
export function seedValues(form, elementsByStep, { zone } = {}) {
	const inputs = {};
	(form?.inputs || []).forEach(input => { inputs[input.key] = getElementInitialValue(inputAsElement(input), { isNewRecord: true, zone }); });
	const steps = {};
	(form?.steps || []).forEach(step => {
		const asked = askedElementsOf(step, elementsByStep[step.key]);
		const count = step.repeat ? Math.max(step.repeat.min || 0, 1) : 1;
		steps[step.key] = Array.from({ length: count }, () => newInstance(asked, zone));
	});
	return { inputs, steps };
}

/** The starting include state: required steps on, optional steps off. */
export function seedIncluded(form) {
	const out = {};
	(form?.steps || []).forEach(step => { out[step.key] = !!step.required; });
	return out;
}

/**
 * Whether a step "has values": some asked element in some instance holds a
 * value that is not blank AND differs from the element's seeded default —
 * a date that defaults to today is not something the user entered, so it
 * does not count. This is what turns an optional step on and off as the
 * user types. The seed is the instance's own (`seeded`, see newInstance);
 * an instance without one compares against the default as computed now.
 */
export function stepHasValues(asked, instances, { zone } = {}) {
	return (instances || []).some(instance => (asked || []).some(el => {
		const value = instance?.elements?.[el.name];
		if (isElementValueEmpty(el, value)) return false;
		const seeded = instance?.seeded ? instance.seeded[el.name] : getElementInitialValue(el, { isNewRecord: true, zone });
		return !sameValue(value, seeded);
	}));
}

/**
 * The keys of the inputs the page must have a value for: those marked
 * required on the form, plus those an INCLUDED step maps into a required
 * element (the API would refuse the row otherwise). An input that only
 * feeds excluded steps is free to stay blank. Form order.
 */
export function requiredInputKeys(form, stepsMeta, included) {
	const needed = new Set();
	(form?.steps || []).forEach(step => {
		if (!included?.[step.key]) return;
		const merged = stepsMeta?.[step.key]?.merged || [];
		Object.entries(step.fields || {}).forEach(([name, mapping]) => {
			if (mapping?.from !== 'input' || !mapping.input) return;
			const element = merged.find(el => el?.name === name);
			if (element?.options?.required) needed.add(mapping.input);
		});
	});
	return (form?.inputs || [])
		.filter(input => input?.options?.required || needed.has(input.key))
		.map(input => input.key);
}

/**
 * The mappings that cannot be honoured as the switches stand: an INCLUDED
 * step takes a required element from a step that is OFF (`element` copies
 * and `step` references alike resolve to blank on the API, which would
 * answer a row error). One entry per broken mapping, with the message the
 * panel shows. Submit is blocked while any exist.
 */
export function blockedDependencies(form, stepsMeta, included) {
	const out = [];
	const steps = form?.steps || [];
	steps.forEach(step => {
		if (!included?.[step.key]) return;
		const merged = stepsMeta?.[step.key]?.merged || [];
		Object.entries(step.fields || {}).forEach(([name, mapping]) => {
			if (mapping?.from !== 'element' && mapping?.from !== 'step') return;
			const source = steps.find(s => s.key === mapping.step);
			if (!source || included[source.key]) return;
			const element = merged.find(el => el?.name === name);
			if (!element?.options?.required) return;
			const sourceLabel = stepLabelOf(source, stepsMeta);
			out.push({
				step: step.key,
				element: name,
				sourceStep: source.key,
				message: `${labelOf(element)} needs the ${sourceLabel} step — turn it on or fill it in.`,
			});
		});
	});
	return out;
}

/** Wraps the page so a relaunch (another form) mounts a fresh one. */
export function MultiAddPageRoute() {
	const { formId } = useParams();
	return <MultiAddPage key={normalizeId(formId) || ''} />;
}

export default function MultiAddPage() {
	const { collectionId, formId } = useParams();
	const navigate = useNavigate();
	const location = useLocation();
	const { message } = App.useApp();
	const collectionContext = useCollectionContext();
	const collection = collectionContext?.collection || null;
	// The collection's zone for the seeds (B11), once it is known.
	const zone = collectionZone(collection);
	const zoneKnown = collectionZoneKnown(collectionContext);
	const collectionAccess = useCollectionAccess();
	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	const unitSystem = useUnitSystem();

	const [form, setForm] = useState(null);
	// 'loading' | 'ready' | 'error' (the read failed: Retry) | 'missing' (the API
	// answered that it is not there, or not this person's).
	const [loadState, setLoadState] = useState('loading');
	const [loadAttempt, setLoadAttempt] = useState(0);
	const [values, setValues] = useState({ inputs: {}, steps: {} });
	// The switches: optional steps only. A required step is always in (see
	// `included` below), so the frame before the seed lands reads right.
	const [includedState, setIncluded] = useState({});
	const [validationErrors, setValidationErrors] = useState({});
	const [rowErrors, setRowErrors] = useState([]);
	const [partial, setPartial] = useState(null);
	// Each step's tags (T9): `{ [stepKey]: { tagIds, privateTagIds, note } }`
	// — the step's own tags as the API judged them for THIS person here (GET
	// /api/tags/usable), shown on the step before the submit, each one the
	// person may leave off. Hand tags ride each instance's `tagIds`; private
	// tags go on through the person's own store once the items exist.
	const [stepTags, setStepTags] = useState({});
	// THE ACTIVE TAG on every step (docs/plans/tags-active-tag-proposal.md,
	// b48: "multi add (every step)"): the person's active tag, as it was when
	// the page opened, joins each step's private tags — shown on the step's
	// Tags line before the submit, marked "Your active tag", and left off a
	// step by its ✕ (`activeLeftOff`). It goes on with the step's own private
	// tags, through the person's store once the items exist.
	const { activeTag, ready: activeTagReady } = useActiveTag();
	const activeTagDoneRef = useRef(false);
	const [activeTagId, setActiveTagId] = useState(null);
	const [activeLeftOff, setActiveLeftOff] = useState({});
	useEffect(() => {
		if (activeTagDoneRef.current || !activeTagReady) return;
		activeTagDoneRef.current = true;
		setActiveTagId(activeTag?.tagId || null);
	}, [activeTagReady, activeTag]);
	/** A step's private tags as they will land: its own, and the active tag unless left off there. */
	const stepPrivateTagIds = useCallback((stepKey) => {
		const own = stepTags[stepKey]?.privateTagIds || [];
		if (!activeTagId || activeLeftOff[stepKey] || own.includes(activeTagId)) return own;
		return [...own, activeTagId];
	}, [stepTags, activeTagId, activeLeftOff]);
	const submittingRef = useRef(false);
	const [submitting, setSubmitting] = useState(false);
	const { withSignature, refusalOf, dialog: signatureDialog } = useMultiAddSignature({ collectionId });

	// The latest values, readable from an event handler without waiting for
	// a render: the auto include decision needs the step's values as they
	// will be after this edit.
	const valuesRef = useRef(values);
	valuesRef.current = values;

	const stateName = location.state?.multiAdd?.name || '';
	const itemsPath = `/collections/${collectionId}/items`;

	// Load the form. A 403/404 (not the caller's to see, or gone) is said in
	// place with the way back to the items list; any other failure is an error
	// with Retry — never a blank page, and never a toast and a redirect.
	useEffect(() => {
		if (!collectionId || !formId) return undefined;
		let active = true;
		setLoadState('loading');
		getMultiAddForm(collectionId, formId)
			.then(loaded => { if (active) { setForm(loaded); setLoadState('ready'); } })
			.catch(error => {
				if (!active) return;
				setLoadState([403, 404].includes(error?.response?.status) ? 'missing' : 'error');
			});
		return () => { active = false; };
	}, [collectionId, formId, loadAttempt]);

	// The steps' tags, asked once the form is here (one read per step that has any).
	useEffect(() => {
		if (!form || !collectionId) return undefined;
		let active = true;
		(form.steps || []).forEach(step => {
			const ids = Array.isArray(step.tagIds) ? step.tagIds : [];
			if (!ids.length) return;
			getUsableTags(collectionId, ids)
				.then(launch => {
					if (!active) return;
					setStepTags(prev => ({ ...prev, [step.key]: { tagIds: launch.tagIds, privateTagIds: launch.privateTagIds, note: launchTagsNote(launch.dropped, 'this step') } }));
				})
				.catch(() => {
					if (!active) return;
					setStepTags(prev => ({ ...prev, [step.key]: { tagIds: [], privateTagIds: [], note: 'This step’s tags could not be checked, so none will be put on.' } }));
				});
		});
		return () => { active = false; };
	}, [form, collectionId]);

	/** The person leaves one of a step's tags off (its chip's ✕). */
	const removeStepTag = useCallback((stepKey, entry) => {
		// The active tag: off this step only, the others keep it.
		if (entry.private && entry.id === activeTagId) setActiveLeftOff(prev => ({ ...prev, [stepKey]: true }));
		setStepTags(prev => {
			// A step with no tags of its own showed only the active tag: nothing more to take off.
			if (!prev[stepKey]) return prev;
			const current = prev[stepKey];
			return {
				...prev,
				[stepKey]: entry.private
					? { ...current, privateTagIds: current.privateTagIds.filter(id => id !== entry.id) }
					: { ...current, tagIds: current.tagIds.filter(id => id !== entry.id) },
			};
		});
	}, [activeTagId]);

	// Per step: its card and merged (inherited) elements, from the app cache.
	const stepsMeta = useMemo(() => {
		if (!form) return null;
		const out = {};
		form.steps.forEach(step => {
			const cardId = normalizeId(step.card_id);
			const card = cardsById?.[cardId] || null;
			const merged = card
				? mergedElementsForCard({ ownElements: card.elements || [], parentCardId: card.parentCardId, getElementsForCard })
				: [];
			out[step.key] = { card, cardId, merged, asked: askedElementsOf(step, merged), filled: filledElementsOf(step, merged) };
		});
		return out;
	}, [form, cardsById, getElementsForCard]);

	const missingCards = useMemo(
		() => (form && stepsMeta ? form.steps.filter(step => !stepsMeta[step.key]?.card) : []),
		[form, stepsMeta]
	);
	const unwritableCards = useMemo(
		() => (form && stepsMeta && collection
			? form.steps.filter(step => stepsMeta[step.key]?.card && !collectionAccess.canWriteCard(stepsMeta[step.key].cardId))
			: []),
		[form, stepsMeta, collection, collectionAccess]
	);

	// What goes in as the switches stand, required steps always on.
	const included = useMemo(() => {
		const out = {};
		(form?.steps || []).forEach(step => { out[step.key] = !!step.required || !!includedState[step.key]; });
		return out;
	}, [form, includedState]);

	// A switch flipped by hand is an edit too (a step with nothing to enter
	// can only be added that way), so it counts toward dirty.
	const dirtySnapshot = useMemo(() => ({ values, included }), [values, included]);
	const { isDirty, resetDirty, guardDiscard } = useDirtyForm(dirtySnapshot);
	const resetDirtyRef = useRef(resetDirty);
	useEffect(() => { resetDirtyRef.current = resetDirty; }, [resetDirty]);

	// Seed once the form and the card cache are both here.
	const seededRef = useRef(false);
	useEffect(() => {
		if (seededRef.current || !form || !stepsMeta || cardsLoading || !zoneKnown) return;
		seededRef.current = true;
		const elementsByStep = {};
		Object.entries(stepsMeta).forEach(([key, meta]) => { elementsByStep[key] = meta.merged; });
		const seeded = seedValues(form, elementsByStep, { zone });
		const seededIncluded = seedIncluded(form);
		setValues(seeded);
		setIncluded(seededIncluded);
		resetDirtyRef.current({ values: seeded, included: seededIncluded });
	}, [form, stepsMeta, cardsLoading, zone, zoneKnown]);

	// ---- seeded steps ------------------------------------------------------
	//
	// A step may take its LINES from the data: one instance per item its
	// query selects, pre-filled from that item. The query can read the
	// inputs, so the lines are re-fetched when an input changes — that is a
	// new context, not an edit, and the lines belong to it. Everything the
	// user typed into a seeded step is replaced along with them, which is why
	// re-seeding is tied to the inputs and nothing else.
	const seededSteps = useMemo(
		() => (form?.steps || []).filter(step => step.seedFrom).map(step => step.key),
		[ form ]
	);
	const inputsKey = useMemo(() => JSON.stringify(values.inputs || {}), [ values.inputs ]);
	const [ seeding, setSeeding ] = useState(false);
	const [ seedWarnings, setSeedWarnings ] = useState([]);

	useEffect(() => {
		if (!form || !stepsMeta || cardsLoading || !seededSteps.length) return undefined;
		let active = true;
		setSeeding(true);
		seedMultiAddForm(collectionId, formId, values.inputs || {})
			.then(result => {
				if (!active) return;
				setSeedWarnings(Array.isArray(result?.warnings) ? result.warnings : []);
				setValues(prev => {
					const steps = { ...prev.steps };
					seededSteps.forEach(key => {
						const lines = Array.isArray(result?.steps?.[ key ]) ? result.steps[ key ] : [];
						const asked = stepsMeta[ key ]?.asked || [];
						steps[ key ] = lines.length
							? lines.map(line => {
								const elements = { ...seedElements(asked, zone), ...(line.elements || {}) };
								// `seeded` is what "Filled for you" reads: a
								// seeded value is filled FOR the user, and
								// says so, exactly like a mapped one.
								return { elements, seeded: elements, seededFrom: line.seededFrom };
							})
							: [];
					});
					return { ...prev, steps };
				});
				// A seeded step with lines is a step the user means to submit.
				setIncluded(prev => {
					const next = { ...prev };
					seededSteps.forEach(key => {
						const has = Array.isArray(result?.steps?.[ key ]) && result.steps[ key ].length;
						if (has) next[ key ] = true;
					});
					return next;
				});
			})
			.catch(() => { if (active) setSeedWarnings([ { message: 'The lines could not be filled in — enter them by hand.' } ]); })
			.finally(() => { if (active) setSeeding(false); });
		return () => { active = false; };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ form, stepsMeta, cardsLoading, seededSteps, collectionId, formId, inputsKey, zone ]);

	const updateInput = useCallback((key, value) => {
		setValues(prev => ({ ...prev, inputs: { ...prev.inputs, [key]: value } }));
		setValidationErrors(prev => (prev[fieldKey(null, null, key)] ? { ...prev, [fieldKey(null, null, key)]: undefined } : prev));
	}, []);

	const withValue = (list, index, name, value) => list.map((instance, i) => (
		i === index ? { ...instance, elements: { ...instance.elements, [name]: value } } : instance
	));

	const updateInstance = useCallback((stepKey, index, name, value) => {
		setValues(prev => ({ ...prev, steps: { ...prev.steps, [stepKey]: withValue(prev.steps[stepKey] || [], index, name, value) } }));
		setValidationErrors(prev => (prev[fieldKey(stepKey, index, name)] ? { ...prev, [fieldKey(stepKey, index, name)]: undefined } : prev));
		// An optional step follows the typing: values in → on, all values out
		// → off. A required step's switch never moves.
		const step = form?.steps.find(s => s.key === stepKey);
		if (!step || step.required) return;
		const next = withValue(valuesRef.current.steps[stepKey] || [], index, name, value);
		const has = stepHasValues(stepsMeta?.[stepKey]?.asked || [], next, { zone });
		setIncluded(prev => (prev[stepKey] === has ? prev : { ...prev, [stepKey]: has }));
	}, [form, stepsMeta, zone]);

	const setStepIncluded = useCallback((step, on) => {
		if (!step || step.required) return;
		setIncluded(prev => (prev[step.key] === on ? prev : { ...prev, [step.key]: on }));
		// A step turned off keeps its typing (the panel only goes muted), but
		// its field errors no longer apply.
		if (!on) {
			setValidationErrors(prev => {
				const next = { ...prev };
				Object.keys(next).forEach(k => { if (k.startsWith(`${step.key}:`)) delete next[k]; });
				return next;
			});
		}
	}, []);

	const addInstance = useCallback((step) => {
		const asked = stepsMeta?.[step.key]?.asked || [];
		setValues(prev => {
			const list = prev.steps[step.key] || [];
			if (step.repeat && list.length >= step.repeat.max) return prev;
			return { ...prev, steps: { ...prev.steps, [step.key]: [...list, newInstance(asked, zone)] } };
		});
	}, [stepsMeta, zone]);

	const removeInstance = useCallback((step, index) => {
		const before = valuesRef.current.steps[step.key] || [];
		if (step.repeat && before.length <= (step.repeat.min || 0)) return;
		const after = before.filter((_, i) => i !== index);
		setValues(prev => ({ ...prev, steps: { ...prev.steps, [step.key]: after } }));
		// Removing the instance that held the step's only values clears the
		// step, and a cleared optional step is off — the same rule as clearing
		// its last field. Removing an empty instance changes nothing.
		if (!step.required) {
			const asked = stepsMeta?.[step.key]?.asked || [];
			if (stepHasValues(asked, before, { zone }) && !stepHasValues(asked, after, { zone })) {
				setIncluded(prev => ({ ...prev, [step.key]: false }));
			}
		}
		// Errors are keyed by position; a removal shifts them, so clear this step's.
		setValidationErrors(prev => {
			const next = { ...prev };
			Object.keys(next).forEach(k => { if (k.startsWith(`${step.key}:`)) delete next[k]; });
			return next;
		});
	}, [stepsMeta, zone]);

	const scrollTo = (step, index, name) => {
		document.getElementById(fieldDomId(step, index, name))?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
	};

	// Derived from the switches: which inputs must be filled, and which
	// mappings the switches leave unhonourable.
	const requiredInputs = useMemo(
		() => new Set(form && stepsMeta ? requiredInputKeys(form, stepsMeta, included) : []),
		[form, stepsMeta, included]
	);
	const blockedDeps = useMemo(
		() => (form && stepsMeta ? blockedDependencies(form, stepsMeta, included) : []),
		[form, stepsMeta, included]
	);

	/** Required inputs and required asked elements of INCLUDED steps, per instance. Returns the error map. */
	const findMissing = useCallback(() => {
		const errors = {};
		let first = null;
		(form?.inputs || []).forEach(input => {
			if (requiredInputs.has(input.key) && isBlank(values.inputs[input.key])) {
				errors[fieldKey(null, null, input.key)] = `${input.label || input.key} is required.`;
				first = first || [null, null, input.key];
			}
		});
		form?.steps.forEach(step => {
			if (!included[step.key]) return;
			const asked = stepsMeta?.[step.key]?.asked || [];
			(values.steps[step.key] || []).forEach((instance, index) => {
				getMissingRequiredElements(asked, instance.elements).forEach(el => {
					errors[fieldKey(step.key, index, el.name)] = `${labelOf(el)} is required.`;
					first = first || [step.key, index, el.name];
				});
			});
		});
		return { errors, first };
	}, [form, stepsMeta, values, included, requiredInputs]);

	// An excluded step goes as an explicit `[]` — skipped, no item.
	const buildPayload = useCallback(() => {
		const steps = {};
		form.steps.forEach(step => {
			if (!included[step.key]) { steps[step.key] = []; return; }
			const asked = new Set((stepsMeta?.[step.key]?.asked || []).map(el => el.name));
			steps[step.key] = (values.steps[step.key] || []).map(instance => {
				const elements = {};
				Object.entries(instance.elements || {}).forEach(([name, value]) => {
					if (asked.has(name) && !isBlank(value)) elements[name] = value;
				});
				// The step's hand tags as the person kept them (T9).
				const hand = stepTags[step.key]?.tagIds || [];
				return hand.length ? { elements, tagIds: hand } : { elements };
			});
		});
		const inputs = {};
		Object.entries(values.inputs || {}).forEach(([key, value]) => { if (!isBlank(value)) inputs[key] = value; });
		return { inputs, steps };
	}, [form, stepsMeta, values, included, stepTags]);

	/**
	 * The steps' PRIVATE tags, once the items exist (T9): the person's own
	 * store, as the item form does. The items are saved either way; a refusal
	 * here is said, never undone.
	 */
	const applyStepPrivateTags = useCallback(async (items) => {
		const rows = [];
		(Array.isArray(items) ? items : []).forEach(entry => {
			stepPrivateTagIds(entry.step).forEach(tagId => rows.push({ tagId, targetType: 'item', target_id: entry.id }));
		});
		if (!rows.length) return;
		try {
			await applyPrivateTags(rows);
		} catch (error) {
			message.warning(`The items were added, but their private tags were not: ${privateTagRefusal(error)}`);
		}
	}, [stepPrivateTagIds, message]);

	const totalItems = useMemo(
		() => (form ? form.steps.reduce((n, step) => n + (included[step.key] ? (values.steps[step.key] || []).length : 0), 0) : 0),
		[form, values, included]
	);

	const resetForRepeat = useCallback(() => {
		const elementsByStep = {};
		Object.entries(stepsMeta || {}).forEach(([key, meta]) => { elementsByStep[key] = meta.merged; });
		const seeded = seedValues(form, elementsByStep, { zone });
		const seededIncluded = seedIncluded(form);
		setValues(seeded);
		setIncluded(seededIncluded);
		setValidationErrors({});
		setRowErrors([]);
		resetDirtyRef.current({ values: seeded, included: seededIncluded });
		window.scrollTo?.({ top: 0, behavior: 'smooth' });
	}, [form, stepsMeta, zone]);

	const applyRowErrors = useCallback((errors) => {
		const fieldErrors = {};
		const rowLevel = [];
		let first = null;
		// Only an ASKED element (or an input) has a field to mark; an error on
		// a mapped element — a fixed choice outside the set, a lookup the
		// card re-pointed — must still be read, so it goes to the row list
		// with the element's label.
		const rendered = (step, name) => {
			if (step === null) return (form?.inputs || []).some(input => input.key === name);
			return (stepsMeta?.[step]?.asked || []).some(el => el.name === name);
		};
		(errors || []).forEach(e => {
			const step = e.step ?? null;
			if (e.element && rendered(step, e.element)) {
				fieldErrors[fieldKey(step, e.index ?? null, e.element)] = e.message;
				first = first || [step, e.index ?? null, e.element];
			} else if (e.element === 'tagIds') {
				// A step's tag the API refused (T9): said as the step's tags.
				rowLevel.push({ ...e, message: `Tags: ${e.message}` });
			} else if (e.element) {
				const el = step === null ? null : (stepsMeta?.[step]?.merged || []).find(x => x.name === e.element);
				rowLevel.push({ ...e, message: `${labelOf(el) || e.element}: ${e.message}` });
			} else {
				rowLevel.push(e);
			}
		});
		setValidationErrors(fieldErrors);
		setRowErrors(rowLevel);
		if (first) scrollTo(...first);
	}, [form, stepsMeta]);

	const handleSubmit = useCallback(async (startAnother = false) => {
		if (!form || !stepsMeta || submittingRef.current || partial) return;
		if (missingCards.length || unwritableCards.length) return;
		// Nothing to add, or a mapping the switches cannot honour: the panels
		// already say so; the shortcut path lands here too.
		if (!totalItems || blockedDeps.length) return;
		const { errors, first } = findMissing();
		if (first) {
			setValidationErrors(errors);
			scrollTo(...first);
			return;
		}
		setValidationErrors({});
		setRowErrors([]);
		submittingRef.current = true;
		setSubmitting(true);
		try {
			// One body for the first send and the signed resend (KPR-1725).
			const payload = buildPayload();
			const result = await withSignature(
				config => submitMultiAddForm(collectionId, formId, payload, config),
				cardIds => itemsOnCards(form, included, values, cardIds) || totalItems);
			if (result?.partial && !(Array.isArray(result.items) && result.items.length)) {
				// The FIRST write failed: nothing exists, so a retry duplicates
				// nothing — an ordinary failure, the form stays live.
				setRowErrors([{ step: result.failed?.step ?? null, index: result.failed?.index ?? null, element: null, message: result.failed?.message || 'The save failed. Nothing was added.' }]);
				message.error('The save failed. Nothing was added.');
				return;
			}
			// The steps' private tags on what was written (T9).
			await applyStepPrivateTags(result?.items);
			if (result?.partial) {
				// Settle the in-flight state first so the locked page never
				// shows a spinner beside its warning.
				submittingRef.current = false;
				setSubmitting(false);
				setPartial(result);
				resetDirtyRef.current({ values, included });
				message.warning('Some items were added, then a save failed. Nothing after it was written.');
				return;
			}
			const count = Array.isArray(result?.items) ? result.items.length : 0;
			message.success(`Added ${count} item${count === 1 ? '' : 's'}.`);
			if (startAnother) {
				resetForRepeat();
				return;
			}
			resetDirtyRef.current({ values, included });
			navigate(itemsPath);
		} catch (error) {
			const refused = refusalOf(error);
			if (refused) { message[refused.level](refused.text); return; }
			const data = error?.response?.data;
			if (data?.code === 'rows' && Array.isArray(data.errors)) {
				applyRowErrors(data.errors);
				message.error('Some values could not be saved. Nothing was added.');
			} else {
				message.error(data?.message || 'Something went wrong. Nothing was added.');
			}
		} finally {
			submittingRef.current = false;
			setSubmitting(false);
		}
	}, [form, stepsMeta, partial, missingCards.length, unwritableCards.length, totalItems, blockedDeps.length, findMissing, collectionId, formId, buildPayload, values, included, message, navigate, itemsPath, resetForRepeat, applyRowErrors, applyStepPrivateTags, withSignature, refusalOf]);

	const handleCancel = useCallback(() => {
		// Asks before dropping unsaved typing.
		guardDiscard(() => navigate(itemsPath));
	}, [guardDiscard, navigate, itemsPath]);

	// The side panel's legend, and ⋯ → Keyboard shortcuts, from one place.
	useScreenShortcuts('Multi add', [
		{ keys: ['mod', 'enter'], label: 'Add the items' },
		{ keys: ['mod', 'shift', 'enter'], label: 'Add and start another' },
		{ keys: ['esc'], label: 'Cancel' },
	]);

	// Form shortcuts: ⌘↵ add · ⌘⇧↵ add & start another · Esc cancel.
	const shortcutRef = useRef(null);
	shortcutRef.current = { handleSubmit, handleCancel };
	useEffect(() => {
		const handler = (event) => {
			const current = shortcutRef.current;
			if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
				event.preventDefault();
				current.handleSubmit(event.shiftKey);
			} else if (event.key === 'Escape') {
				if (escapeBelongsElsewhere(event)) return;
				event.preventDefault();
				current.handleCancel();
			}
		};
		document.addEventListener('keydown', handler);
		return () => document.removeEventListener('keydown', handler);
	}, []);

	// The lookup pool for the "filled for you" previews: every live value
	// as a pseudo item of its step's card (the inputs under a pseudo card of
	// their own), so a picked lookup reads as the item's title rather than
	// its id. useLinkedItems fetches only ids it has not seen; a keystroke
	// elsewhere costs it a scan and nothing more.
	const inputElements = useMemo(() => (form?.inputs || []).map(inputAsElement), [form]);
	const lookupElementsForCard = useCallback(
		cardId => (cardId === INPUTS_CARD ? inputElements : getElementsForCard(cardId)),
		[inputElements, getElementsForCard]
	);
	// A fixed lookup value has no field it is picked in, so it joins the pool
	// from the mapping itself — as a pseudo item of the step's card, like the
	// live values. Per step: { [element]: value } for its static lookups.
	const staticLookups = useMemo(() => {
		const out = {};
		(form?.steps || []).forEach(step => {
			const elements = {};
			(stepsMeta?.[step.key]?.filled || []).forEach(el => {
				const mapping = step.fields?.[el.name];
				if (el.dataType === 'card-lookup' && mapping?.from === 'static' && !isBlank(mapping.value)) elements[el.name] = mapping.value;
			});
			if (Object.keys(elements).length) out[step.key] = elements;
		});
		return out;
	}, [form, stepsMeta]);
	const hasLookups = useMemo(() => (
		inputElements.some(el => el.dataType === 'card-lookup')
		|| Object.values(stepsMeta || {}).some(meta => meta.asked.some(el => el.dataType === 'card-lookup'))
		|| Object.keys(staticLookups).length > 0
	), [inputElements, stepsMeta, staticLookups]);
	const pseudoItems = useMemo(() => {
		if (!form || !stepsMeta || !hasLookups) return [];
		const out = [{ _id: null, card_id: INPUTS_CARD, elements: values.inputs }];
		form.steps.forEach(step => {
			const cardId = stepsMeta[step.key]?.cardId;
			if (staticLookups[step.key]) out.push({ _id: null, card_id: cardId, elements: staticLookups[step.key] });
			(values.steps[step.key] || []).forEach(instance => {
				out.push({ _id: null, card_id: cardId, elements: instance.elements });
			});
		});
		return out;
	}, [form, stepsMeta, values, hasLookups, staticLookups]);
	const itemsById = useLinkedItems({
		items: pseudoItems,
		getElementsForCard: lookupElementsForCard,
		enabled: hasLookups && !cardsLoading,
		resetKey: collectionId,
	});
	const resolveLookup = useMemo(
		() => createLookupResolver({ itemsById, cardsById, getElementsForCard }),
		[itemsById, cardsById, getElementsForCard]
	);
	const previewExtras = useMemo(
		() => ({ stepsMeta, resolveLookup, prefs: { unitSystem } }),
		[stepsMeta, resolveLookup, unitSystem]
	);

	const stepCards = useMemo(() => (form ? stepCardsOf(form, cardsById) : []), [form, cardsById]);
	const title = form?.name || stateName || 'Multi add';
	const ready = loadState === 'ready' && form && stepsMeta && !cardsLoading;
	const blocked = missingCards.length > 0 || unwritableCards.length > 0;
	const submitDisabled = !ready || blocked || submitting || !!partial || !totalItems || blockedDeps.length > 0;

	// The form's actions, defined once: the context bar shows them at the top,
	// FormActionsFooter repeats the same node at the end of the main column.
	const formActions = (
		<>
			<Button onClick={handleCancel}>Cancel</Button>
			<Button
				className="item-form-save-another"
				onClick={() => handleSubmit(true)}
				disabled={submitDisabled || !isDirty}
			>
				Add & start another
			</Button>
			<Button
				type="primary"
				onClick={() => handleSubmit(false)}
				disabled={submitDisabled || !isDirty}
				loading={submitting}
			>
				Add {totalItems} item{totalItems === 1 ? '' : 's'}
			</Button>
		</>
	);

	const renderField = (element, value, onChange, step, index, cardMeta) => {
		const key = fieldKey(step, index, element.name);
		return (
			<div
				key={key}
				id={fieldDomId(step, index, element.name)}
				className={`item-form-field ${fieldWidthClass(element)}`}
			>
				<EventFormElement
					event={{ _id: null, collection_id: collectionId, elements: value.all }}
					element={{ ...element, name: scopedName(element.name, step, index) }}
					value={value.current}
					onChange={(name, next) => onChange(unscopedName(name), next)}
					cardMeta={cardMeta}
					showIconBadge={false}
					errorMessage={validationErrors[key] || null}
				/>
			</div>
		);
	};

	// The steps line: every step by name, repeats marked ×, excluded ones
	// greyed — the same wording as describeSteps, one span per step.
	const stepsLine = form ? form.steps.map((step, i) => {
		const card = stepsMeta?.[step.key]?.card;
		const name = step.repeat
			? `${step.repeat.label || step.label || card?.name || 'card'} ×`
			: (step.label || card?.name || 'card');
		return (
			<React.Fragment key={step.key}>
				{i ? ', ' : ''}
				<span className={`multi-add-page__step-name${included[step.key] ? '' : ' multi-add-page__step-name--off'}`}>{name}</span>
			</React.Fragment>
		);
	}) : null;

	return (
		<div className="item-form-page multi-add-page">
			<ContextBar
				breadcrumb={collectionCrumbs(collection, collectionId, 'Multi add')}
				records={stepCards}
				kind="card"
				title={title}
				below={form ? (
					<span className="multi-add-page__steps-line" data-testid="multi-add-steps-line">
						<STACK aria-hidden="true" />
						{' '}Adds {totalItems} item{totalItems === 1 ? '' : 's'}: {stepsLine}
					</span>
				) : null}
				actions={formActions}
			/>

			{partial ? (
				<Alert
					type="warning"
					showIcon
					className="multi-add-page__partial"
					message="Some items were added, then a save failed"
					description={(
						<div className="multi-add-page__partial-body">
							<div>{partial.failed?.message}</div>
							<div className="multi-add-page__partial-items">
								{partial.items.map(created => {
									const card = cardsById?.[normalizeId(created.card_id)];
									return (
										<RecordBadge
											key={created.id}
											kind="item"
											record={card}
											size="sm"
											title={created.displayValue || card?.name || 'Item'}
											to={`/collections/${collectionId}/items/${created.id}`}
										/>
									);
								})}
							</div>
							<div>Add the rest by hand from the items list; submitting again would add these items twice.</div>
							<Button size="small" onClick={() => navigate(itemsPath)}>Go to items</Button>
						</div>
					)}
				/>
			) : null}

			{rowErrors.length ? (
				<Alert
					type="error"
					showIcon
					className="multi-add-page__row-errors"
					message="Some values could not be saved"
					description={(
						<ul>
							{rowErrors.map((e, i) => (
								<li key={i}>
									{e.step ? <span className="multi-add-page__row-where">{e.step}{e.index != null ? ` #${e.index + 1}` : ''}: </span> : null}
									<span>{e.message}</span>
								</li>
							))}
						</ul>
					)}
				/>
			) : null}

			{ready && missingCards.length ? (
				<Alert type="warning" showIcon message="This multi add uses a card you cannot see." description="Ask a collection manager to check its steps." />
			) : null}
			{ready && unwritableCards.length ? (
				<Alert
					type="info"
					showIcon
					message="View only"
					description={`You cannot add ${unwritableCards.map(step => stepsMeta[step.key]?.card?.name || 'that card').join(', ')} items in this collection, so this multi add cannot be submitted.`}
				/>
			) : null}

			<div className="item-form-columns">
				<Form className="item-form-main" disabled={blocked || !!partial}>
					{!ready ? (
						loadState === 'missing' ? (
							<Alert
								type="error"
								showIcon
								message="That multi add is not available."
								description="It may have been deleted, or it is not yours to use."
								action={<Button onClick={() => navigate(itemsPath)}>Go to items</Button>}
							/>
						) : (
							<DataState
								state={deriveDataState({
									loading: loadState === 'loading' || (loadState === 'ready' && (!form || !stepsMeta || cardsLoading)),
									error: loadState === 'error' ? 'Check your connection and try again.' : null,
								})}
								skeleton={<Skeleton variant="form-field" count={4} />}
								error={loadState === 'error' ? 'Check your connection and try again.' : null}
								onRetry={() => setLoadAttempt(n => n + 1)}
								label="this multi add"
							/>
						)
					) : (
						<>
							{form.inputs.length ? (
								<section className="form-panel multi-add-inputs" data-testid="multi-add-inputs">
									<div className="micro-label form-panel__kicker">Inputs</div>
									<div className="item-form-fields">
										{form.inputs.map(input => {
											const base = inputAsElement(input);
											// Required as the switches make it (see requiredInputKeys),
											// so the label's marker and the check agree.
											const element = { ...base, options: { ...base.options, required: requiredInputs.has(input.key) } };
											return renderField(
												element,
												{ current: values.inputs[input.key], all: values.inputs },
												(name, value) => updateInput(name, value),
												null, null, null
											);
										})}
									</div>
								</section>
							) : null}

							{/* Seeded steps take their LINES from the data, and the
							    query can read the inputs above: say when they are
							    being fetched and when one could not be. */}
							{seeding ? (
								<Alert
									type="info"
									showIcon
									icon={<Spin size="small" />}
									message="Filling in the lines…"
								/>
							) : null}
							{seedWarnings.length ? (
								<Alert
									type="warning"
									showIcon
									message="Some lines were not filled in"
									description={seedWarnings.map(warning => warning.message).join(' ')}
								/>
							) : null}

							{form.steps.map(step => {
								const meta = stepsMeta[step.key];
								const card = meta?.card;
								const instances = values.steps[step.key] || [];
								const stepLabel = stepLabelOf(step, stepsMeta);
								const instanceLabel = step.repeat?.label || stepLabel;
								const on = !!included[step.key];
								const rows = filledRowsOf(step, meta, values, included, form, previewExtras);
								const deps = blockedDeps.filter(d => d.step === step.key);
								const atMax = step.repeat ? instances.length >= step.repeat.max : true;
								const atMin = step.repeat ? instances.length <= (step.repeat.min || 0) : true;

								const renderInstance = (instance, index) => (
									<div className="item-form-fields">
										{meta.asked.length ? meta.asked.map(element => renderField(
											element,
											{ current: instance.elements?.[element.name], all: instance.elements },
											(name, value) => updateInstance(step.key, index, name, value),
											step.key, index, card
										)) : (
											<div className="form-panel__hint">Nothing to enter — every field is filled for you.</div>
										)}
									</div>
								);

								return (
									<section
										className={`form-panel multi-add-step${on ? '' : ' multi-add-step--off'}${step.required ? ' multi-add-step--required' : ''}`}
										key={step.key}
										data-testid={`multi-add-step-${step.key}`}
									>
										<div className="form-panel__kicker-row multi-add-step__kicker-row">
											<div className="micro-label form-panel__kicker multi-add-step__kicker">
												{card ? <RecordBadge kind="card" record={card} size="xs" title={stepLabel} /> : stepLabel}
												{step.repeat ? <span className="form-panel__count">{instances.length}</span> : null}
											</div>
											<div className="multi-add-step__tools">
												{step.repeat ? (
													<Button
														size="small"
														icon={<PLUS />}
														onClick={() => addInstance(step)}
														disabled={atMax}
														title={atMax ? `At most ${step.repeat.max}` : undefined}
													>
														Add another {instanceLabel.toLowerCase()}
													</Button>
												) : null}
												<span className="multi-add-step__include">
													<Switch
														size="small"
														checked={on}
														disabled={!!step.required}
														aria-label={`Include ${stepLabel}`}
														onChange={checked => setStepIncluded(step, checked)}
													/>
													<span>Include</span>
												</span>
											</div>
										</div>
										{/* Only a REQUIRED step explains itself, because its Include
											switch is disabled and that needs a reason. An optional step
											wears a switch that is already off and already labelled —
											repeating "optional, turn it on" under every card was noise. */}
										{step.required ? (
											<div className="form-panel__hint multi-add-step__include-hint">
												Required — every submission adds this card.
											</div>
										) : null}
										{deps.map(dep => (
											<div key={dep.element} className="form-panel__hint multi-add-step__dependency" role="alert">{dep.message}</div>
										))}
										<div className="multi-add-step__body">
											{stepTags[step.key] || stepPrivateTagIds(step.key).length ? (
												<LaunchTagsRow
													collectionId={collectionId}
													tagIds={stepTags[step.key]?.tagIds || []}
													privateTagIds={stepPrivateTagIds(step.key)}
													note={stepTags[step.key]?.note || null}
													onRemove={entry => removeStepTag(step.key, entry)}
													onDismissNote={() => setStepTags(prev => ({ ...prev, [step.key]: { ...prev[step.key], note: null } }))}
													label={`Tags${step.repeat ? ` · every ${instanceLabel.toLowerCase()}` : ''}`}
													testId={`multi-add-tags-${step.key}`}
													activeTagId={activeTagId && activeTag?.tagId === activeTagId ? activeTagId : null}
												/>
											) : null}
											{rows.length ? (
												<div className="multi-add-step__filled-wrap">
													<div className="micro-label multi-add-step__filled-kicker">
														Filled for you{step.repeat ? ` · every ${instanceLabel.toLowerCase()}` : ''}
													</div>
													<dl className="multi-add-step__filled" data-testid={`multi-add-filled-${step.key}`}>
														{rows.map(row => (
															<React.Fragment key={row.name}>
																<dt>{row.label}</dt>
																<dd className={row.muted ? 'multi-add-step__filled-muted' : undefined}>
																	{row.text}
																	{row.note ? <span className="multi-add-step__filled-note"> · {row.note}</span> : null}
																</dd>
															</React.Fragment>
														))}
													</dl>
												</div>
											) : null}
											{step.repeat ? instances.map((instance, index) => (
												<div className="multi-add-instance" key={index} data-testid={`multi-add-instance-${step.key}-${index}`}>
													<div className="multi-add-instance__head">
														<span className="multi-add-instance__label">{instanceLabel} {index + 1}</span>
														<Button
															type="text"
															size="small"
															icon={<TRASH />}
															aria-label={`Remove ${instanceLabel.toLowerCase()} ${index + 1}`}
															onClick={() => removeInstance(step, index)}
															disabled={atMin}
														/>
													</div>
													{renderInstance(instance, index)}
												</div>
											)) : (instances[0] ? renderInstance(instances[0], 0) : null)}
										</div>
									</section>
								);
							})}

							<FormActionsFooter>{formActions}</FormActionsFooter>
						</>
					)}
				</Form>

				<aside className="item-form-side">
					{form ? (
						<section className="form-panel">
							<div className="micro-label form-panel__kicker">What this adds</div>
							<ul className="multi-add-side__steps">
								{form.steps.map(step => {
									const card = stepsMeta?.[step.key]?.card;
									const count = (values.steps[step.key] || []).length;
									const on = !!included[step.key];
									const label = stepLabelOf(step, stepsMeta);
									const badge = card ? <RecordBadge kind="card" record={card} size="sm" title={label} /> : label;
									const inner = (
										<>
											{badge}
											{step.required ? <span className="multi-add-side__required">required</span> : null}
											{step.repeat && on ? <span className="multi-add-side__count">×{count}</span> : null}
										</>
									);
									return (
										<li
											key={step.key}
											className={`multi-add-side__step${on ? '' : ' multi-add-side__step--off'}${step.required ? ' multi-add-side__step--required' : ''}`}
											data-testid={`multi-add-side-${step.key}`}
										>
											{step.required ? inner : (
												<button
													type="button"
													className="multi-add-side__toggle"
													aria-pressed={on}
													onClick={() => setStepIncluded(step, !on)}
												>
													{inner}
												</button>
											)}
										</li>
									);
								})}
							</ul>
							{form.linkItems ? <div className="form-panel__hint">The items are related to each other on save.</div> : null}
						</section>
					) : null}
					<div className="item-form-shortcuts">
						<span><kbd>⌘↵</kbd> Add</span>
						<span><kbd>⌘⇧↵</kbd> Add & start another</span>
						<span><kbd>Esc</kbd> Cancel</span>
					</div>
				</aside>
			</div>
			{signatureDialog}
		</div>
	);
}
